/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { HttpHeaders } from '@angular/common/http';
import { type OnDestroy, type OnInit, Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
    type BaseRequestOptionsArgs, type ColumnDef, type ErrMessage, type FormGroup,
    type HttpResponse, type Result, type TableConfig, DataTableComponent, DMValidators,
    FormBuilder, FormControl, LazyloadService, PermissionService, PromptService, TimeService,
    ToastService, TransferTableComponent, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { syncSha256 } from '@dm/web-crypto-utils';
import { AuthService, ToolService } from 'app/common/services';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { type Subscriber, type Subscription, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, finalize } from 'rxjs/operators';

import { type ExportItems } from './export.const';
import {
    type errorResult, type ExpansionBoard, type ExportLogBtn,
    type ExportLogParamItem, type ExportLogTimeOutItem,
    DownloadStatus, ErrorCode, ExportNamespace, IncludeManageLog, LogType,
    NodeType, PackagePerformanceFileStatus, PackageType, ResultCode,
} from './export.enum';
import { ExportService } from './export.service';
import { ExportStrService } from './export-str.service';

const timeoutMap = {
    runningData: {
        getpath: 790 * 1e3,
        download: 180 * 1e3,
    },
    eraseData: {
        interval: 5 * 1e3,
        getpath: 60 * 1e3,
        download: 180 * 1e3,
    },
    diagnose: {
        interval: 5 * 1e3,
        package: 185 * 1e3,
        getpath: 60 * 1e3,
        check: 185 * 1e3,
        max: 10 * 60e3,
    },
    log: {
        interval: 5 * 1e3,
        package: 90 * 1e3,
        getpath: 10 * 1e3,
        check: 200 * 1e3,
    },
    antivirus: {
        interval: 5 * 1e3,
        package: 90 * 1e3,
        getpath: 10 * 1e3,
        check: 200 * 1e3,
    },
    performanceFile: {
        interval: 5 * 1e3,
        package: 60 * 1e3,
        getpath: 60 * 1e3,
        check: 60 * 1e3,
        // 最大超时时间6小时
        max: 6 * 60 * 60 * 1e3,
    },
    ftds: {
        interval: 5 * 1e3,
        package: 90 * 1e3,
        getpath: 10 * 1e3,
        check: 200 * 1e3,
    },
};
interface NodeData {
    description: string;
    name: string;
    nodeid: string;
    result: number;
    failFileList?: string;
    history_file: Array<{ [key: string]: string }>;
}

@Component({
    selector: 'app-export',
    templateUrl: './export.component.html',
    styleUrls: ['./export.component.scss'],
})
export class ExportComponent implements OnInit, OnDestroy {
    @Input() show = true;
    @Input() label;
    @ViewChild('dt') dt: DataTableComponent;
    @ViewChild('transferTable') transferTable: TransferTableComponent;
    @Output() doAction = new EventEmitter();
    @ViewChild('template') template: TemplateRef<void>;
    SWITCH = SWITCH;
    FeatureConfig = FeatureConfig;
    IncludeManageLog = IncludeManageLog;
    /**是否按节点导出 */
    isExportByNode = false;

    // 界面资料
    i18n = this.str.i18nLabel;
    loading = false;
    logType = LogType;
    downloadStatus = DownloadStatus;
    /** 导出的卡片 */
    exportItems: ExportItems[] = [];

    /** 是否支持防病毒日志导出 */
    isSurportAntivirus = true;

    /** 默认选中运行数据 */
    selectedType: LogType;

    /**
     * shard开关
     */
    ShardSwitch = ShardSwitch;
    ProductConfig = ProductConfig;

    /**
     * 控制所有下载按钮的忙等和隐藏
     */
    btnMap: { [key: string]: ExportLogBtn } = {
        [LogType.runningData]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.system]: {
            label: this.i18n.downloadRecent,
            icon: 'icon-download',
            originLabel: this.i18n.downloadRecent,
            loading: false,
            visible: true,
            tip: '',
        },
        [`${LogType.system}all`]: {
            label: this.i18n.downloadAll,
            icon: 'icon-download',
            originLabel: this.i18n.downloadAll,
            loading: false,
            visible: true,
            tip: '',
        },
        [`${LogType.system}private`]: {
            label: this.i18n.downloadPrivate,
            icon: 'icon-download',
            originLabel: this.i18n.downloadPrivate,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.dha]: {
            label: this.i18n.downloadDHA,
            icon: 'icon-download',
            originLabel: this.i18n.downloadDHA,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.hssd]: {
            label: this.i18n.downloadHSSD,
            icon: 'icon-download',
            originLabel: this.i18n.downloadHSSD,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.erase]: {
            label: this.i18n.downloadEraseReport,
            icon: 'icon-download',
            originLabel: this.i18n.downloadEraseReport,
            loading: false,
            visible: true,
        },
        [LogType.diagnostic]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.cloudBackup]: {
            label: this.i18n.downloadRecent,
            icon: 'icon-download',
            originLabel: this.i18n.downloadRecent,
            loading: false,
            visible: true,
        },
        [LogType.cloudBackup + 'all']: {
            label: this.i18n.downloadAll,
            icon: 'icon-download',
            originLabel: this.i18n.downloadAll,
            loading: false,
            visible: true,
        },
        [LogType.antivirus]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
        },
        [LogType.performanceFile]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
            tip: '',
        },
        [LogType.ftds]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
            tip: '',
        },
        //硬盘数据
        [LogType.DISK_RUNING_STATUS_DATA]: {
            label: this.i18n.download,
            icon: 'icon-download',
            originLabel: this.i18n.download,
            loading: false,
            visible: true,
            tip: '',
        },
    };

    /**
     * 是否正在取消“导出性能文件”
     */
    isCancelExportPerf = false;

    icon = {
        success: '#icon-status-succeed',
        failed: '#icon-status-failed',
    };

    /** 日志类型的相关配置项 */
    logConfig = {
        [LogType.system]: {
            url: ExportNamespace.systemLog,
            prefix: 'sys_log_',
            count: 0,
            maxTime: 720,  // 720为约60分钟
        },
        [LogType.dha]: {
            url: ExportNamespace.dhaLog,
            prefix: 'dha_log_',
            count: 0,
            maxTime: 720,
        },
        [LogType.hssd]: {
            url: ExportNamespace.hssdLog,
            prefix: 'hssd_log_',
            count: 0,
            maxTime: 720,
        },
        [LogType.erase]: {
            url: ExportNamespace.eraseLog,
            prefix: 'erase_log_',
            count: 0,
            maxTime: 720,
        },
        [LogType.cloudBackup]: {
            url: ExportNamespace.cloudBackupLog,
            prefix: 'cloudbackup_log_',
            count: 0,
            maxTime: 240,
        },
        [LogType.antivirus]: {
            url: ExportNamespace.antivirusLog,
            prefix: 'antivirus_log_',
            count: 0,
            maxTime: 720,  // 720为约60分钟
        },
        [LogType.ftds]: {
            url: ExportNamespace.ftdsLog,
            prefix: 'ftds_log_',
            count: 0,
            maxTime: 720,  // 720为约60分钟
        },
    };

    // 性能文件不能下载的状态及原因
    cannotDownloadPerformanceFileStatus = {
        [PackagePerformanceFileStatus.downloading]: this.i18n.exportFailedDownloadingStatus,
        [PackagePerformanceFileStatus.fault]: this.i18n.exportFailedFaultStatus,
        [PackagePerformanceFileStatus.none]: this.i18n.exportFailedNoneStatus,
        [PackagePerformanceFileStatus.failed]: this.i18n.exportFailedFailStatus,
    };

    // system，disk表格数据
    // 表格相关配置项
    /** 表格相关配置项 */
    tableColumnDefs: ColumnDef[];
    tableConfig: TableConfig;
    tableData = [];
    tableLoading = false;
    isExportingDiagnose = false;

    /** 导出日志提示 */
    exportLogTip: string;
    showLogTab = false;
    /** 当前正在导出的日志类别 */
    currentLogType;
    /** 导日志getpath超时次数，连续3次，弹出错误 */
    getPathTimeoutCount = 0;
    /** 是否正在查询下载路径 */
    isGettingPath = false;
    /** 是否正在检查能否下载 */
    isChecking = false;
    /** 是否正在导出日志 */
    isExportingLog = false;
    /** 导出日志，轮询查询下载路径 */
    getpathTimer;
    /** 是否下载全部 */
    isDownloadAll = false;
    isDownloadPrivate = false;
    /** 是否正在下载运行数据 */
    isExportingRunningData = false;
    /** 是否正在下载数据擦除报告 */
    isExportingEraseData = false;
    /** 下载系统日志进度 */
    downloadProcess = 0;
    /** 当前正在下载的节点列表。key为节点id，value为下载的文件名列表。包含控制器和IP框 */
    downloadNodes: { [node: string]: string[] } = {};
    /**
     * 是否正在下载硬盘数据
     */
    isExportingDiskData = false;

    diagnoseSubscription: Subscription;
    performanceFileSubscription: Subscription;

    /** 运行数据 诊断日志显示下载结果 */
    resultMap = {
        [LogType.system]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.disk]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.runningData]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.diagnostic]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.antivirus]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.performanceFile]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.ftds]: {
            show: false,
            isOk: true,
            result: '',
        },
        [LogType.DISK_RUNING_STATUS_DATA]: {
            show: false,
            isOk: true,
            result: '',
        },
    };

    /** 表格状态显示 */
    statusMap = {
        [DownloadStatus.failed]: this.i18n.failed,
        [DownloadStatus.success]: this.i18n.success,
        [DownloadStatus.loading]: this.i18n.loading,
    };

    /**
     * 导出数据高危提示信息表
     */
    chooseTypeActionMsgMap = {
        [LogType.runningData]: this.i18n.runningMsg,
        [LogType.diagnostic]: this.i18n.warningMsg,
        [LogType.erase]: this.i18n.eraseMsg,
        [LogType.performanceFile]: this.i18n.perfFileExportMsg,
    };

    isChrome = true;

    formGroup: FormGroup;

    keyTips;

    errorMessage: any;

    hasConfigPMS: boolean;

    isExportingPerformanceFile = false;

    // 默认显示7天间隔
    DefalutDateDiff = 6 * 24 * 60 * 60 * 1000;
    dateRange = [this.timeService.currTime * 1e3 - this.DefalutDateDiff, this.timeService.currTime * 1e3];

    isTimeRangeValid = true;
    nodeData: Array<ExpansionBoard & Controller> = [];
    nodeColumnDefs = [];
    nodeSelection = [];
    /** 高危提示框里的是否导出管理面日志 */
    isExportAllInPrompt = IncludeManageLog.NOT;

    /**节点的表格 */
    nodeTableConfig: TableConfig;
    recentBtnDisable = false;
    /**按节点，全部日志导出，高危提示内下方的信息 */
    includeManageLog: string;
    suportNodeExportBtnDisable = false;
    /**硬盘信息，各个框上硬盘数量 */
    diskMap: Map<string, number>;
    sysAllLogTipBefore: string;
    get showTable(): boolean {
        // 1.日志类
        // 2.日志类，进行了下载，
        //      下载过程中：切换时，下载的日志类别和当前日志类别相同显示，不同不显示
        //      下载完成后：切换其他，再切换回来，表格不显示
        const logTypeArr = [
            LogType.system,
            LogType.disk,
            LogType.cloudBackup,
            LogType.antivirus,
            LogType.ftds,
        ];

        const selectLog = logTypeArr.some((logType) => this.selectedType === logType);

        return selectLog && this.showLogTab;
    }

    get recentBtnDisableTip(): string {
        if (this.nodeSelection.length > 100) {
            return this.i18n.numUpLimit;
        } else if (this.nodeSelection.some((item: ExpansionBoard | Controller) => item.TYPE === NodeType.EXPANSION_BOARD)) {
            return this.i18n.exportRecentLogErrTip;
        } else {
            return '';
        }
    }

    get eraseBtnDisableTip(): string {
        return this.nodeSelection.length ? this.i18n.exportEraseLogByNodesTtip : '';
    }

    get numUpLimitTip(): string {
        return this.nodeSelection.length > 100 ? this.i18n.numUpLimit : '';
    }

    dontGetNodeData = FeatureConfig.IsHideExportLogByNode ||
        !this.permissionService.checkShow(['controller_R', 'expansion_module_R', 'disk_R']);

    get hideExportByNodeData(): void {
        return this.dontGetNodeData || ![this.logType.system, this.logType.disk].includes(this.selectedType);
    }

    constructor(
        public toast: ToastService,
        public prompt: PromptService,
        public str: ExportStrService,
        public exportService: ExportService,
        public lazyLoadSerevice: LazyloadService,
        public fb: FormBuilder,
        public authService: AuthService,
        public router: Router,
        public permissionService: PermissionService,
        public timeService: TimeService,
        public utilService: UtilService,
        public toolService: ToolService,
    ) { }

    /** 获取浏览器类型 */
    checkIsChrome(): void {
        // 取得浏览器的userAgent字符串
        const userAgent = navigator.userAgent;
        this.isChrome = userAgent.indexOf('Chrome') > -1;
    }

    exportByNodeChange(status: boolean): void {
        this.controlSuportNodeExportBtn();
        if (status && this.nodeSelection.length) {
            this.getTip(this.nodeSelection);
        } else {
            this.getTip(this.nodeData);
        }
    }

    getTimeTip(logType: string): number {
        const { time, diskTime } = this.str.btnTipConfig[logType];
        if (time) {
            return time;
        }

        if (diskTime) {
            if (this.nodeSelection.length && this.isExportByNode) {
                // 每个引擎（控制框、硬盘框）下每个硬盘的时间，同时选中两个引擎的控制器或级联模块，按最大值计算
                // 选中的如果引擎没有硬盘，按最大值计算
                let maxTemp = 0;
                const locationSetTemp = new Set(this.nodeSelection.map(
                    (item: ExpansionBoard | Controller) => item.LOCATION.substring(0, item.LOCATION.indexOf('.'))));
                locationSetTemp.forEach((item: string) => {
                    if (this.diskMap.get(item) > maxTemp) {
                        maxTemp = this.diskMap.get(item);
                    }
                });
                if (maxTemp) {
                    return maxTemp * diskTime;
                } else {
                    return this.getMaxDiskTime(diskTime);
                }
            }
            return this.getMaxDiskTime(diskTime);
        }

        return undefined as number;
    }

    getMaxDiskTime(diskTime: number): number {
        let maxTemp = 0;

        this.diskMap.forEach((value: number) => {
            if (value > maxTemp) {
                maxTemp = value;
            }
        });
        return maxTemp * diskTime;
    }

    getSizeTip(logType: string, nodeList: Array<ExpansionBoard | Controller>): number {
        const { totalSize, controllers = 0, expansionBoard = 0 } = this.str.btnTipConfig[logType];
        if (totalSize) {
            return totalSize;
        }

        // 根据控制器&级联模块计算大小
        const tempSizeValue = (nodeList.filter((item: ExpansionBoard | Controller) => item.TYPE === NodeType.CONTROLLER).length * controllers) +
            (nodeList.filter((item: ExpansionBoard | Controller) => item.TYPE === NodeType.EXPANSION_BOARD).length * expansionBoard);

        // 判断是否为整型，非整型场景取两个精度
        return Number.isInteger(tempSizeValue) ? tempSizeValue : Number(tempSizeValue.toFixed(2));
    }

    getTip(nodeList: Array<ExpansionBoard | Controller>): void {
        Object.keys(this.str.btnTipConfig).forEach((item: string) => {
            if (this.btnMap[item]) {
                this.btnMap[item].tip =
                    this.i18n.duration + this.i18n.aboutTime(this.getTimeTip(item).toString()) +
                    '<br>' +
                    this.i18n.size + this.i18n.aboutSize(this.getSizeTip(item, nodeList).toString());
            }
        });
    }

    nodeSelectionChange(): void {
        this.getTip(this.nodeSelection);
        this.controlSuportNodeExportBtn();
    }

    controlSuportNodeExportBtn(): void {
        this.suportNodeExportBtnDisable = this.isExportByNode && (!this.nodeSelection.length || this.nodeSelection.length > 100);
        this.recentBtnDisable = this.suportNodeExportBtnDisable ||
            (this.isExportByNode && this.nodeSelection.some(
                (item: ExpansionBoard | Controller) => item.TYPE === NodeType.EXPANSION_BOARD));
    }

    initColumnDef(): void {
        this.nodeColumnDefs = [
            {
                field: 'LOCATION',
                header: this.i18n.name,
                filterType: 'search',
                search: true,
            },
            {
                field: 'TYPE',
                header: this.i18n.type,
                filterType: 'dropdown',
                options: this.str.nodeType,
                render: (value: number) => this.utilService.getLabelFromOptions(this.str.nodeType, value),
            },
        ];
    }

    initNodeTableConfig(): void {
        this.nodeTableConfig = {
            dataKey: 'LOCATION',
            checkRowDisabled: (item: ExpansionBoard & Controller) => ({ disabled: item.disableCheck }),
        };
    }

    setNodeData(): void {
        if (this.dontGetNodeData) {
            return;
        }
        this.loading = true;
        forkJoin([
            this.exportService.getControllers().pipe(
                catchError(() => {
                    return of(null);
                }),
            ),
            this.exportService.getExpboards().pipe(
                catchError(() => {
                    return of(null);
                }),
            ),
            this.exportService.getDiskInfo().pipe(
                catchError(() => {
                    return of(null);
                }),
            ),
        ]).subscribe((res: HttpResponse[]) => {
            const controllersData = res[0]?.data || [];
            const expboardData = res[1]?.data || [];
            // 级联模块MODEL为0代表SAS型号，其没有日志
            this.nodeData = controllersData.concat(expboardData.filter((item: ExpansionBoard) => item.MODEL !== '0'));
            this.setDiskMap(res[2]?.data || []);
            this.initColumnDef();
            this.initNodeTableConfig();
            this.getTip(this.nodeData);
        },
            () => {
                this.loading = false;
            },
            () => {
                this.loading = false;
            });
    }

    setDiskMap(diskData: Disk[]): void {
        // 盘按控制框或硬盘框分组
        const diskMapTemp = new Map();
        diskData.forEach((item: Disk) => {
            const locationInd = item.LOCATION.indexOf('.');
            const locationTemp = item.LOCATION.substring(0, locationInd);
            if (diskMapTemp.get(locationTemp)) {
                diskMapTemp.set(locationTemp, diskMapTemp.get(locationTemp) + 1);
            } else {
                diskMapTemp.set(locationTemp, 1);
            }
        });
        this.diskMap = diskMapTemp;
    }

    ngOnInit(): void {
        this.checkIsChrome();

        this.init();

        this.initErrors();

        this.initFormGroup();

        this.initKeyTips();

        this.setNodeData();
    }

    /**
     * 取消性能文件的订阅
     */
    unsubscriptPerf(): void {
        if (this.performanceFileSubscription) {
            this.performanceFileSubscription.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        if (this.getpathTimer) {
            clearInterval(this.getpathTimer);
        }
        if (this.diagnoseSubscription) {
            this.diagnoseSubscription.unsubscribe();
        }

        this.unsubscriptPerf();

        // 禁用超时检测例外
        this.authService.disableTimeoutExcept();
    }

    init(): void {
        this.isSurportAntivirus = SWITCH.SUPPORT_NAS && ProductConfig.isSupportAntivirus;

        this.initExportItems();

        const exportItemsTypes = this.exportItems.map((value) => value.type);
        if (exportItemsTypes.includes(LogType.runningData) && exportItemsTypes.includes(LogType.system)) {
            const logGenre = this.label === 'upgrade' ? LogType.runningData : LogType.system;
            // 默认显示系统日志, 升级跳转显示配置信息日志
            this.onClickItem(logGenre);
        } else {
            this.onClickItem(exportItemsTypes[0]);
        }


        this.tableConfig = {
            rows: null,
            motion: false,
        };

        this.tableColumnDefs = [
            {
                field: 'name',
                header: this.i18n.sourceLabel,
            },
            {
                field: 'nodeId',
                header: this.i18n.nodeLabel,
                ignore: true,
            },
            {
                field: 'status',
                header: this.i18n.statusLabel,
                width: '160px',
            },
        ];
    }

    initErrors(): void {
        // 输入框错误提示信息
        this.errorMessage = {
            key: {
                required: this.str.encryptPasswordRequiredTip(this.i18n.encryptPasswordLab),
                rangeByteLength: this.str.passwordLengthTip(8, 16),
                complex: this.i18n.keyComplexErrorTip,
            },
            confirmKey: {
                required: this.str.encryptConfirmPasswordRequiredTip(this.i18n.encryptConfirmPasswordLab),
                equal: this.i18n.equalTip,
            },
        };
    }

    initKeyTips(): void {
        const keyLengthTip = this.str.passwordLengthTip(8, 16);
        const keyConmplexTip = this.i18n.keyComplexTip;
        const complexTip = this.i18n.complexTip;
        this.keyTips = [keyLengthTip, complexTip, keyConmplexTip];
    }

    // 导出性能文件需要查看是否配置pms
    setPMSConfig(): void {
        this.exportService.getPmsLun().subscribe(({ data }) => {
            this.hasConfigPMS = data.selected;
        }, () => {
            // 除了查到的是false,其余情况都是true
            this.hasConfigPMS = true;
        });
    }

    ondateRangeChange(): void {
        const [startTime, endTime] = this.dateRange;
        // 时间范围不能大于30天
        const DataRangeLimit = 29 * 24 * 60 * 60 * 1000;
        if ((endTime - startTime) > DataRangeLimit) {
            this.isTimeRangeValid = false;
        } else {
            this.isTimeRangeValid = true;
        }
    }


    initFormGroup(): void {
        this.formGroup = this.fb.group({
            encryption: new FormControl(false),
            key: new FormControl('', {
                validators: [
                    Validators.required,
                    DMValidators.rangeByteLength(8, 16),
                    DMValidators.complex('middle'),
                ],
                updateOn: 'blur',
            }),
            confirmKey: new FormControl('', {
                validators: [Validators.required],
                updateOn: 'blur',
            }),
        }, {
            validators: [
                DMValidators.equal(['key', 'confirmKey']),
            ],
        });
    }


    restInput(): void {
        if (!this.formGroup.value.encryption) {
            this.initFormGroup();
        }
    }

    // 可以导出的类型
    initExportItems(): void {
        this.exportItems = [
            {
                type: LogType.system,
                label: this.i18n.systemLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-system',
                permissions: ['alarm_W'],
            },
            {
                type: LogType.disk,
                label: this.i18n.diskLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-disk',
                permissions: ['alarm_W'],
            },
            //硬盘数据
            {
                type: LogType.DISK_RUNING_STATUS_DATA,
                label: this.i18n.diskRunningStatusLable,
                isLoading: false,
                tips: '',
                icon: '#app-icon-disk-download',
                permissions: ['disk_R'],
            },
            {
                type: LogType.runningData,
                label: this.i18n.runningDataLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-running-data',
                permissions: ['running_data_W'],
            },
            {
                type: LogType.diagnostic,
                label: this.i18n.diagnosticLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-diagnose',
                permissions: ['alarm_W'],
            },
            {
                type: LogType.antivirus,
                label: this.i18n.antivirusLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-antivirus',
                permissions: ['alarm_W'],
            },
            {
                type: LogType.ftds,
                label: this.i18n.ftdsLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-ftds',
                permissions: ['alarm_W'],
            },
            {
                type: LogType.performanceFile,
                label: this.i18n.performanceFileLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-log-performance',
                permissions: ['performance_W'],
            },
            {
                type: LogType.streamlineDiagnostic,
                label: this.i18n.streamlineDiagnosticLabel,
                isLoading: false,
                tips: '',
                icon: '#app-icon-streamline-diagnostic',
                permissions: ['alarm_W'],
            },
        ].filter((item) =>
            this.permissionService.checkShow(item.permissions),
        );

        // 不支持NAS或者二级存储，屏蔽防病毒日志导出
        if (!this.isSurportAntivirus) {
            this.exportItems = this.exportItems.filter(({ type }) => type !== LogType.antivirus);
        }
        // 注意：系统日志、硬盘日志、FTDS日志、防病毒日志导出存在互斥关系，不同条件屏蔽需要同时修改对应卡片提示信息

        // 微存储屏蔽性能文件
        if (FeatureConfig.IsHidePms) {
            this.exportItems = this.exportItems.filter(({ type }) => type !== LogType.performanceFile);
        }

        // 非超级管理员不展示精简故障信息
        if (!this.permissionService.isSuperAdmin()) {
            this.exportItems = this.exportItems.filter(({ type }) => type !== LogType.streamlineDiagnostic);
        }
    }

    // 切换下载对象类型
    onClickItem(type: LogType): void {
        if (type === LogType.performanceFile) {
            this.setPMSConfig();
        } else {
            this.hasConfigPMS = false;
        }
        if (this.selectedType === type) {
            return;
        }
        this.selectedType = type;
        if (this.selectedType === this.currentLogType) {
            this.showLogTab = this.isExportingLog;
        } else {
            this.showLogTab = false;
        }

        // 待定：切换类型时是否清除下载失败提示
        this.hideResult([
            LogType.system,
            LogType.disk,
            LogType.runningData,
            LogType.diagnostic,
            LogType.antivirus,
            LogType.performanceFile,
            LogType.ftds,
            LogType.DISK_RUNING_STATUS_DATA,
        ]);
    }

    getSizeIncludeManageLog(): void {
        this.includeManageLog = this.i18n.totalDuration + this.i18n.aboutTime(
            this.getTimeTip(LogType.system + 'allInPrompt').toString()) +
            '<br>' +
            this.i18n.totalSize + this.i18n.aboutSize(this.getSizeTip(LogType.system + 'allInPrompt', this.nodeSelection).toString());
    }

    // 下载按钮入口
    onDownload(logType: LogType, isAll?: boolean, isPrivate?: boolean): void {
        if (isAll) {
            this.getSizeIncludeManageLog();
        }

        if (logType === LogType.performanceFile) {
            this.chooseTypeAction(logType, isAll, false, isPrivate);
            return;
        }

        //导出硬盘数据
        if (logType === LogType.DISK_RUNING_STATUS_DATA) {
            this.getAllDiskData();
            return;
        }

        if (this.formGroup.value.encryption) {
            this.formGroup.validate((valid) => {
                if (!valid) {
                    return;
                }

                this.chooseTypeAction(logType, isAll, true, isPrivate);
            });
        } else {
            this.chooseTypeAction(logType, isAll, false, isPrivate);
        }
    }

    /**
     * 查询所有硬盘数据
     */
    getAllDiskData(): void {
        this.btnMap[LogType.DISK_RUNING_STATUS_DATA].loading = true;
        this.isExportingDiskData = true;
        this.setCardLoading(LogType.DISK_RUNING_STATUS_DATA, true);

        this.exportService.getDisksBatch().subscribe((data: Disk[]) => {
            this.handDiskData(data);
            this.isExportingDiskData = false;
            this.hideResult([LogType.DISK_RUNING_STATUS_DATA]);
            this.setCardLoading(LogType.DISK_RUNING_STATUS_DATA, false);
            this.btnMap[LogType.DISK_RUNING_STATUS_DATA].loading = false;
        }, () => {
            this.isExportingDiskData = false;
            this.showResult(LogType.DISK_RUNING_STATUS_DATA, false);
            this.setCardLoading(LogType.DISK_RUNING_STATUS_DATA, false);
            this.btnMap[LogType.DISK_RUNING_STATUS_DATA].loading = false;
        });
    }

    chooseTypeAction(logType: LogType, isAll = false, isFormValue: boolean = undefined, isPrivate: boolean = undefined): void {
        const msg = this.chooseTypeActionMsgMap[logType] || this.i18n.warningMsgOneHour;
        let doAction;
        // 使用downloadAntivirusOrFtdsLog和package方法进行导出的数据不能同时进行
        let isExportingLogConflict = false;

        switch (logType) {
            case LogType.runningData:
                doAction = (): void => this.downloadRunningData(isFormValue);
                break;
            case LogType.diagnostic:
                doAction = (): void => this.downloadDiagnostic(isFormValue);
                break;
            case LogType.erase:
                doAction = (): void => this.downloadEraseData(LogType.erase, isFormValue);
                break;
            case LogType.antivirus:
                isExportingLogConflict = this.isExportingLog;
                doAction = (): void => this.downloadAntivirusOrFtdsLog(LogType.antivirus, isFormValue);
                break;
            case LogType.ftds:
                isExportingLogConflict = this.isExportingLog;
                doAction = (): void => this.downloadAntivirusOrFtdsLog(LogType.ftds, isFormValue);
                break;
            case LogType.performanceFile:
                doAction = (): void => this.downloadPerformanceFile();
                break;
            default:
                isExportingLogConflict = this.isExportingLog;
                doAction = (): void => this.package(logType, isAll, isFormValue, isPrivate);
        }

        if (isExportingLogConflict) {
            this.showExportConflictPrompt();
            return;
        }

        this.showExportActionPrompt(msg, doAction, logType, isAll);
    }

    /**
     * 展示导出数据高危提示
     */
    showExportActionPrompt(msg: string, doAction: () => void, logType: LogType, isAll: boolean): void {
        this.prompt.warning({
            title: this.i18n.title,
            content: msg,
            id: 'export-prompt',
            bottomTemplateRef: this.showExportAllInPrompt(logType, isAll) ? this.template : null,
            canHidden: false,
            onOk: () => {
                this.prompt.close();
                doAction();

                // 导出日志时，开启超时检测例外
                this.authService.enableTimeoutExcept();
            },
            onCancel: () => this.prompt.close(),
        });
    }

    /**
     * 展示任务冲突提示
     */
    showExportConflictPrompt(): void {
        this.prompt.prompt({
            id: 'export-log-conflict-prompt',
            content: this.i18n.exportConflictPrompt,
            onOk: () => {
                this.prompt.close();
            },
        });
    }

    /**
     * 是否展示按节点，全部日志导出，高危提示内下方的信息
     */
    showExportAllInPrompt(logType: LogType, isAll: boolean): boolean {
        return logType === LogType.system && isAll && this.nodeSelection.length && this.isExportByNode;
    }

    /**
     * 处理盘剩余寿命
     */
    renderDiskRemaiLife(remaiLife: string): string {
        return Number(remaiLife) === -1 ? UNPLACEHOLDER : remaiLife;
    }

    /**
     * 处理盘数据
     */
    handDiskData(diskArr: Disk[]): void {
        // 导出表格表头依次是硬盘SN、型号、类型、类型ID、厂商、FW、容量、厂商容量、bom、21条码、剩余天数、运行时间、sha256
        const headers: string = [
            this.i18n.diskSnLable,
            this.i18n.diskModelLable,
            this.i18n.diskStyleLable,
            this.i18n.diskManufacturerLable,
            this.i18n.diskFWLable,
            this.i18n.diskCapacityLable,
            this.i18n.diskManuCapacityLable,
            this.i18n.diskBomLable,
            this.i18n.diskBarcodeLable,
            this.i18n.diskRemainLifeLable,
            this.i18n.diskRuntimeLable,
        ].join(',');

        // 导出表格内容
        const contents = [];

        diskArr.forEach((disk: Disk) => {
            let row: string[] = [
                disk.SERIALNUMBER,
                disk.MODEL,
                this.utilService.getLabelFromOptions(this.str.diskTypeOpttions, Number(disk.DISKTYPE)),
                disk.MANUFACTURER,
                disk.FIRMWAREVER,
                disk.SECTORS,
                disk.manuCapacity,
                disk.ITEM,
                disk.barcode,
                this.renderDiskRemaiLife(disk.REMAINLIFE),
                disk.RUNTIME,
            ];

            // 计算sha256：SN+容量+厂商容量+BOM+21条码+剩余天数+运行时间
            const originStr =
                `${disk.SERIALNUMBER}${disk.SECTORS}${disk.manuCapacity}${disk.ITEM}${disk.barcode}${this.renderDiskRemaiLife(disk.REMAINLIFE)}${disk.RUNTIME}`;

            const sha256Str = syncSha256(originStr);

            row.push(sha256Str);
            row = row.map((value: string) => this.toolService.handleCsvValue(value));
            contents.push(row);
        });

        this.exportService.exportCsv(this.i18n.diskRunningStatusLable, headers, contents.join('\n'));
    }

    downloadAntivirusOrFtdsLog(logType: LogType.antivirus | LogType.ftds, isFormValue: boolean): void {
        let params: ExportLogParamItem;
        let options: BaseRequestOptionsArgs;
        let currentTimeoutItem: ExportLogTimeOutItem;

        if (logType === LogType.antivirus) {
            currentTimeoutItem = timeoutMap.antivirus;
            params = { antivirus: true };
        } else {
            currentTimeoutItem = timeoutMap.ftds;
        }

        const url = this.logConfig[logType].url;

        if (isFormValue) {
            params = {
                encryption: this.formGroup.value.encryption ? 1 : 0,
                tag: 'package',
                ...params,
            };
            options = {
                headers: new HttpHeaders({ fileEncryptionKey: this.formGroup.value.key }),
            };
        } else {
            params = {
                tag: 'package',
                ...params,
            };
        }

        this.beginTask(logType);
        // 每次开始时
        this.downloadProcess = 0;
        // 记录正在下载的节点
        this.downloadNodes = {};

        options = {
            timeout: currentTimeoutItem.package,
            ...options,
        };

        // 1.打包
        this.exportService.doAction(url, params, options).subscribe(
            () => {
                // 开始打包，设置成25%
                this.downloadProcess = 25;
                this.getPathTimeoutCount = 0;

                this.getpath(logType);

                this.getpathTimer = setInterval(() => {
                    // 如果超过最大等待时间，停止轮询
                    if (this.logConfig[logType].count > this.logConfig[logType].maxTime) {
                        this.handleError(logType, {
                            result: { description: this.i18n.exportTimeout },
                        } as HttpResponse);
                    } else {
                        this.getpath(logType);

                        this.logConfig[logType].count++;
                    }
                }, currentTimeoutItem.interval);
            },
            (error) => {
                this.handleError(logType, error);
            },
        );
    }

    // 导出运行日志：
    // 判断是否加密导出
    // 1.查询下载路径
    // 2.下载数据
    downloadRunningData(isFormValue?: boolean): void {
        this.setButtonLoading(LogType.runningData, true);
        this.hideResult([LogType.runningData]);
        this.setCardLoading(LogType.runningData, true);
        this.isExportingRunningData = true;

        const reset = (): void => {
            this.setButtonLoading(LogType.runningData, false);
            this.setCardLoading(LogType.runningData, false);
            this.isExportingRunningData = false;

            // 请求失败或文件开始下载后，禁用超时检测例外
            this.authService.disableTimeoutExcept();
        };

        let options: BaseRequestOptionsArgs;
        let params = {};
        if (isFormValue) {
            params = {
                encryption: this.formGroup.value.encryption ? 1 : 0,
                tag: 'getpath',
            };
            options = {
                headers: new HttpHeaders({ fileEncryptionKey: this.formGroup.value.key }),
            };
        } else {
            params = { tag: 'getpath' };
        }
        options = {
            timeout: timeoutMap.runningData.getpath,
            ...options,
        };

        this.exportService.exportRunningData(params, options).subscribe(
            (response) => {
                const { data } = response;
                reset();

                this.exportService.download(ExportNamespace.runningData, { tag: data });
            },
            (error) => {
                this.openError(error);
                this.showResult(LogType.runningData, false);
                reset();
            },
        );
    }

    /**
     * 重置数据擦除报告的按钮状态
     * @param type 日志类型
     */
    resetEraseBtn = (type: LogType): void => {
        this.setButtonLoading(type, false);
        this.setCardLoading(type, false);
        this.setButtonVisible(true);
        this.isExportingEraseData = false;

        // 请求失败或文件开始下载后，禁用超时检测例外
        this.authService.disableTimeoutExcept();
    };

    // 导出硬盘数据擦除报告：
    // 0.判断是否加密导出
    // 1.查询下载路径
    // 2.下载数据
    downloadEraseData(type: LogType, isFormValue?: boolean): void {
        this.setButtonVisible(false, type);
        this.setButtonLoading(type, true);
        this.showLogTab = false;
        this.hideResult([LogType.disk]);
        this.setCardLoading(type, true);
        this.isExportingEraseData = true;

        let options: BaseRequestOptionsArgs;
        let params = {};
        if (isFormValue) {
            params = {
                encryption: this.formGroup.value.encryption ? 1 : 0,
                tag: 'package',
            };
            options = {
                headers: new HttpHeaders({ fileEncryptionKey: this.formGroup.value.key }),
            };
        } else {
            params = { tag: 'package' };
        }
        options = {
            timeout: timeoutMap.eraseData.getpath,
            ...options,
        };

        options = {
            timeout: timeoutMap.eraseData.getpath,
            ...options,
        };

        this.exportService.exportEraseData(params, options).subscribe(
            () => {
                const getPath = (): Subscription => this.exportService.exportEraseData({ tag: 'getpath' }, options).subscribe(
                    (response) => {
                        const { data } = response;
                        this.resetEraseBtn(type);
                        clearInterval(exportEraseHandler);

                        this.exportService.download(ExportNamespace.eraseLog, { tag: data });
                    },
                    (error) => {
                        const { result: { code } } = error;
                        if (code === ErrorCode.packing) {
                            return;
                        }

                        clearInterval(exportEraseHandler);
                        this.openError(error);
                        this.showResult(LogType.disk, false);
                        this.resetEraseBtn(type);
                    },
                );

                const exportEraseHandler = setInterval(getPath, timeoutMap.eraseData.interval);
            },
            (error) => {
                this.openError(error);
                this.showResult(LogType.disk, false);
                this.resetEraseBtn(type);
            },
        );
    }

    // 下载诊断文件：
    // 0.判断是否加密导出
    // 1.下发package命令
    // 2.轮询查询下载路径
    // 3.下发check命令，确认是否可下载
    // 4.下载数据
    downloadDiagnostic(isFormValue?: boolean): void {
        this.isExportingDiagnose = true;

        this.setButtonLoading(LogType.diagnostic, true);
        this.hideResult([LogType.diagnostic]);
        this.setCardLoading(LogType.diagnostic, true);

        let options: BaseRequestOptionsArgs;
        let params = {};
        if (isFormValue) {
            params = {
                encryption: this.formGroup.value.encryption ? 1 : 0,
                tag: 'package',
            };
            options = {
                headers: new HttpHeaders({ fileEncryptionKey: this.formGroup.value.key }),
            };
        } else {
            params = { tag: 'package' };
        }
        const package$ = this.exportService.exportDiagnoseData(
            params,
            {
                timeout: timeoutMap.diagnose.package,
                ...options,
            },
        );
        const getpath$ = this.getDiagnosePath();
        const check$ = this.exportService.exportDiagnoseData({ tag: 'check' }, { timeout: timeoutMap.diagnose.check });

        // 恢复按钮和下载状态
        const reset = (): void => {
            this.isExportingDiagnose = false;
            this.setButtonLoading(LogType.diagnostic, false);
            this.setCardLoading(LogType.diagnostic, false);

            // 请求失败或文件开始下载后，禁用超时检测例外
            this.authService.disableTimeoutExcept();
        };

        const result$ = package$
            .pipe(
                concatMap(
                    (response) => {
                        // package请求结果，成功后，查询下载路径
                        const { result: { code } } = response;

                        if (Number(code) !== ErrorCode.ok) {
                            this.openError(response);
                            reset();
                            return of(null);
                        }

                        return getpath$;
                    },
                ),
                concatMap(
                    (response: any) => {
                        if (!response) {
                            return of(null);
                        }

                        const { data, result: { code } } = response;

                        if (Number(code) !== ErrorCode.ok || Number(data.result) !== 0) {
                            this.openError(response);
                            reset();
                            this.showResult(LogType.diagnostic, false);
                            return of(null);
                        }

                        return check$;
                    },
                ),
            );

        this.diagnoseSubscription = result$.subscribe(
            (response) => {
                if (!response) {
                    return;
                }

                reset();

                this.exportService.download(ExportNamespace.diagnoseData, { tag: 'getfile' });
            },
            (error) => {
                reset();
                this.showResult(LogType.diagnostic, false);
                this.openError(error);
            },
        );
    }

    /**
     * 恢复性能文件下载按钮和下载状态
     */
    resetPerfBtn = (): void => {
        this.isExportingPerformanceFile = false;
        this.setButtonLoading(LogType.performanceFile, false);
        this.setCardLoading(LogType.performanceFile, false);

        // 请求失败或文件开始下载后，禁用超时检测例外
        this.authService.disableTimeoutExcept();
    };

    // 下载性能文件：
    // 0.下发package命令
    // 1.轮询查询下载路径
    // 2.下载数据
    downloadPerformanceFile(): void {
        const [startTime, endTime] = this.dateRange;
        const params = { startTime, endTime, packageType: PackageType.zip };

        this.isExportingPerformanceFile = true;
        this.setButtonLoading(LogType.performanceFile, true);
        this.hideResult([LogType.performanceFile]);
        this.setCardLoading(LogType.performanceFile, true);

        const package$ = this.exportService.packagePerformanceData(params, { timeout: timeoutMap.performanceFile.package });
        const getStatus$ = this.getPackagePerformanceFileStatus();
        const result$ = package$.pipe(concatMap(
            (response: HttpResponse) => {
                // package请求结果，成功后，查询下载路径
                const { result: { code } } = response;

                if (Number(code) !== ErrorCode.ok) {
                    this.resetPerfBtn();
                    return of(null);
                }

                return getStatus$;
            },
        ));

        this.performanceFileSubscription = result$.subscribe(
            (response: HttpResponse) => {
                if (!response) {
                    return;
                }
                // eslint-disable-next-line max-len
                this.exportService.downloadPerformanceData({ packageType: PackageType.zip, languageFlag: this.i18n.language === 'ja' ? 'en' : this.i18n.language });
                this.resetPerfBtn();
            },
            (error: ErrMessage & errorResult) => {
                this.resetPerfBtn();
                this.showResult(LogType.performanceFile, false);
                if (error.description) {
                    // 处理自定义报错
                    this.toast.error(error, { safeHtml: true });
                } else if (error.result) {
                    this.openError(error);
                } else {
                    return;
                }
            },
        );
    }

    // 显示下载结果
    showResult(inputLogType: string | number, isSuccess: boolean): void {
        let logType = inputLogType;

        if (logType === LogType.dha || logType === LogType.hssd) {
            logType = LogType.disk;
        }

        this.resultMap[logType].show = true;
        this.resultMap[logType].isOk = isSuccess;
        this.resultMap[logType].result = isSuccess ?
            this.statusMap[DownloadStatus.success] :
            this.statusMap[DownloadStatus.failed];
    }

    // 隐藏下载结果
    hideResult(logTypes: any[]): void {
        logTypes.forEach((item) => {
            if (item === LogType.dha || item === LogType.hssd) {
                this.resultMap[LogType.disk].show = false;
            } else {
                this.resultMap[item].show = false;
            }
        });
    }


    /**
     * 获取控制器id
     */
    getCtrlidList(): void {
        return this.nodeSelection
            .filter((item: ExpansionBoard | Controller) => item.TYPE === NodeType.CONTROLLER)
            .reduce((pre: ExpansionBoard | Controller,
                cur: ExpansionBoard | Controller,
                curInd: number,
                arr: Array<ExpansionBoard | Controller>) =>
                pre + cur.ID + (curInd === arr.length - 1 ? '' : ',')
                , '');
    }

    /**
     * 获取级联模块id
     */
    getEnclosureidList(): void {
        return this.nodeSelection
            .filter((item: ExpansionBoard | Controller) => item.TYPE === NodeType.EXPANSION_BOARD)
            .reduce((pre: ExpansionBoard | Controller,
                cur: ExpansionBoard | Controller,
                curInd: number,
                arr: Array<ExpansionBoard | Controller>) =>
                pre + cur.LOCATION + (curInd === arr.length - 1 ? '' : ',')
                , '');
    }

    /**
     * 控制接口表格能否点击
     */
    controlNodeTableCheck(disableCheck: boolean): void {
        this.nodeData.forEach((item) => item.disableCheck = disableCheck);
        this.transferTable.leftTable.updateData(this.nodeData);
        this.nodeSelection.forEach((item: ExpansionBoard & Controller) => item.disableCheck = disableCheck);
        this.transferTable.rightTable.updateData(this.nodeSelection);
    }

    // 导出系统日志、DHA日志，HSSD日志
    // 0.判断是否加密导出
    // 1.下发package命令
    // 2.轮询查询A控制器下载路径
    // 3.下发check命令，确认是否可下载
    // 4.下载数据
    // 5.重复2-5，下载B控制器数据
    package(logType: LogType, isAll = false, isFormValue?: boolean, isPrivate?: boolean): void {
        const url = this.logConfig[logType].url;
        const param: any = {};
        let options: BaseRequestOptionsArgs;
        let params = {};

        if (isFormValue) {
            params = {
                encryption: this.formGroup.value.encryption ? 1 : 0,
                tag: 'package',
            };
            options = {
                headers: new HttpHeaders({ fileEncryptionKey: this.formGroup.value.key }),
            };
        } else {
            params = { tag: 'package' };
        }

        if (isAll) {
            param.exportall = true;
        }

        // 是否导出私有日志
        if (isPrivate) {
            param.private = true;
        }

        if (this.isExportByNode) {
            const ctrlidList = this.getCtrlidList();
            const enclosureidList = this.getEnclosureidList();
            Object.assign(param, { ctrlidList, enclosureidList });
            this.sysAllLogTipBefore = this.btnMap[`${LogType.system}all`].tip;

            if (logType === LogType.system && isAll) {
                this.getPackageAllSystemLogParams(param);
            }
            this.controlNodeTableCheck(true);
        }
        this.beginTask(logType, isAll, isPrivate);
        // 每次开始时
        this.downloadProcess = 0;
        // 记录正在下载的节点
        this.downloadNodes = {};

        options = {
            timeout: timeoutMap.log.package,
            ...options,
        };

        // 1.打包
        this.exportService.doAction(url, { ...param, ...params }, options).subscribe(
            () => {
                // 开始打包，设置成25%
                this.downloadProcess = 25;
                this.getPathTimeoutCount = 0;

                this.getpath(logType);

                this.getpathTimer = setInterval(() => {
                    // 如果超过最大等待时间，停止轮询
                    if (this.logConfig[logType].count > this.logConfig[logType].maxTime) {
                        this.handleError(logType, {
                            result: { description: this.i18n.exportTimeout },
                        } as HttpResponse);
                    } else {
                        this.getpath(logType);

                        this.logConfig[logType].count++;
                    }
                }, timeoutMap.log.interval);
            },
            (error) => {
                this.handleError(logType, error);
            },
        );
    }

    /**
     * 导出全部系统日志时的请求参数
     */
    getPackageAllSystemLogParams(param: ExportLogParamItem): void {
        if (this.isExportAllInPrompt === IncludeManageLog.IS) {
            Object.assign(param, { unlimitSize: Number(IncludeManageLog.IS) });
            this.btnMap[`${LogType.system}all`].tip =
                `${this.i18n.duration}${this.i18n.aboutTime(this.getTimeTip(`${LogType.system}allInPrompt`).toString())}<br>${this.i18n.size +
                this.i18n.aboutSize(
                    this.getSizeTip(`${LogType.system}allInPrompt`, this.nodeSelection).toString(),
                )}`;
            // 还原为默认值
            this.isExportAllInPrompt = IncludeManageLog.NOT;
        }
    }

    getpath(logType: LogType): void {
        if (this.isGettingPath) {
            return;
        }

        const url = this.logConfig[logType].url;

        this.isGettingPath = true;

        this.exportService.doAction(url, { tag: 'getpath' }).subscribe(
            (response) => {
                // 查询成功，将失败的计数清零
                this.getPathTimeoutCount = 0;

                this.handlePath(logType, response);

                this.isGettingPath = false;
            },
            (error) => {
                this.isGettingPath = false;
                const { result: { code } } = error;

                // code=83887409（日志打包未完成） 时，表示本次查询成功
                if (Number(code) === ErrorCode.packing || Number(code) === ErrorCode.noIPLog) {
                    this.handlePath(logType, error);
                } else {
                    // 记录失败次数，达到3次退出
                    if (this.getPathTimeoutCount++ >= 3) {
                        this.handleError(logType,
                            { result: { description: this.i18n.exportFailedReasonDesc, suggestion: this.i18n.exportFailedSuggestDesc } } as HttpResponse,
                        );
                    }
                }
            },
        );
    }

    handlePath(logType: LogType, getPathResult: HttpResponse): void {
        const { data, result }: { data: NodeData[]; result: Result } = getPathResult;
        const isAllFinished = this.checkCompleted(data) &&
            (Number(result.code) === ErrorCode.ok || Number(result.code) === ErrorCode.noIPLog);

        this.loadTableData(data);

        if (isAllFinished) {
            this.stopTask(logType);
            // 所有检查的节点都是完成状态，设置成100%
            this.downloadProcess = 100;
            return;
        }

        // 检查是否可下载
        const canDownloadNode = data.find((a) => Number(a.result) === ResultCode.LOADING);

        if (canDownloadNode) {
            const nodeId = canDownloadNode.nodeid;
            const fileName = canDownloadNode.name;
            // 如果有状态为可下载的文件且当前未下载，且不在checking状态，则下载;
            // ibase接口内包名和状态两个字段没有在同一个锁上，并发情况下有概率返回不一致的结果，DM界面加个判断fileName规避一下
            const fileNameValue = !this.downloadNodes[nodeId] || !this.downloadNodes[nodeId].includes(fileName);
            if (!this.isChecking && fileName && fileNameValue) {
                this.downloadFile(logType, nodeId, fileName);
            }
            return;
        }

        // 打包成功的节点
        const successNodes: NodeData[] = data.filter(
            (value) => [ResultCode.EXPORT_SUCCESS, ResultCode.EXPORT_PARTLY].includes(Number(value.result)),
        );

        if (successNodes.length) {
            // 打包结束开始下载，设置成75%
            this.downloadProcess = 75;
            this.checkDownload(logType, successNodes[0].nodeid);
        }
    }

    checkDownload(logType: string | number, nodeId: string): void {
        // 如果处于检查状态，不进行下一个检查，必须等上一个结束
        if (this.isChecking) {
            return;
        }

        this.isChecking = true;

        const url = this.logConfig[logType].url;
        const param = { tag: 'check', nid: nodeId };
        this.exportService.doAction(url, param, { timeout: timeoutMap.log.check })
            .subscribe(() => {
                this.isChecking = false;
            }, () => {
                this.isChecking = false;
            });
    }

    downloadFile(logType: string | number, nodeId: string, fileName: string): void {
        const url = this.logConfig[logType].url;
        this.exportService.download(url, { tag: nodeId });
        if (!this.downloadNodes[nodeId]) {
            this.downloadNodes[nodeId] = [];
        }
        this.downloadNodes[nodeId].push(fileName);
    }

    openError(response?: HttpResponse | (ErrMessage & errorResult)): void {
        let description = this.i18n.baseDesc;
        let suggestion = '';

        if (response) {
            description = response.result.description;
            suggestion = response.result.suggestion;
        }

        this.toast.error({ description, suggestion }, this.i18n.baseErr);
    }

    handleError(logType: LogType, result?: HttpResponse, stopTask = true): void {
        // 弹出错误
        this.openError(result);
        this.showResult(logType, false);
        if (stopTask) {
            this.stopTask(logType);
        }
    }

    checkCompleted(data: NodeData[] = []): boolean {
        // 导出完成：所有节点状态都不等于成功、正在导出、部分成功、正在下载
        return data.every((node) => ![ResultCode.EXPORT_SUCCESS, ResultCode.EXPORTING,
        ResultCode.EXPORT_PARTLY, ResultCode.LOADING].includes(Number(node.result)));
    }

    // 确认导出后的一些标记
    beginTask(logType: LogType, isAll?: boolean, isPrivate?: boolean): void {
        // 设置开始导出、当前导出的日志类型、显示表格、清空上次表格数据
        this.hideResult([this.selectedType]);
        this.isExportingLog = true;
        this.setCurrentLogType(logType);
        this.showLogTab = true;
        this.tableLoading = true;
        this.tableData = [];
        this.loadTableData([]);
        this.isDownloadAll = isAll;
        this.isDownloadPrivate = isPrivate;

        // 隐藏其他日志下载按钮
        let downloadBtn: LogType = logType;
        if (isAll) {
            downloadBtn = `${logType}all` as LogType;
        } else if (isPrivate) {
            downloadBtn = `${logType}private` as LogType;
        } else {
            downloadBtn = logType;
        }
        this.setButtonVisible(false, downloadBtn);

        this.setButtonLoading(downloadBtn, true);

        this.setCardLoading(logType, true);

        // 导出日志提示初始化
        this.initLogTip(this.selectedType);
    }

    /**
     * 互斥日志导出卡片提示初始化
     */
    initLogTip(logType: LogType): void {
        const exportDhaLogTip = this.isSurportAntivirus ? this.i18n.exportDhaLogTip : this.i18n.exportDhaLogSanTip;
        const exportSystemLogTip = this.isSurportAntivirus ? this.i18n.exportSystemLogTip : this.i18n.exportSystemLogSanTip;
        const exportFTDSLogTip = this.isSurportAntivirus ? this.i18n.exportFTDSLogTip : this.i18n.exportFTDSLogSanTip;

        switch (logType) {
            case LogType.disk:
                this.findExportItem(LogType.system).tips = exportDhaLogTip;
                this.findExportItem(LogType.ftds).tips = exportDhaLogTip;
                if (this.isSurportAntivirus) {
                    this.findExportItem(LogType.antivirus).tips = exportDhaLogTip;
                }
                break;
            case LogType.system:
                this.findExportItem(LogType.disk).tips = exportSystemLogTip;
                this.findExportItem(LogType.ftds).tips = exportSystemLogTip;
                if (this.isSurportAntivirus) {
                    this.findExportItem(LogType.antivirus).tips = exportSystemLogTip;
                }
                break;
            case LogType.antivirus:
                this.findExportItem(LogType.disk).tips = this.i18n.exportAntinusLogTip;
                this.findExportItem(LogType.system).tips = this.i18n.exportAntinusLogTip;
                this.findExportItem(LogType.ftds).tips = this.i18n.exportAntinusLogTip;
                break;
            case LogType.ftds:
                this.findExportItem(LogType.disk).tips = exportFTDSLogTip;
                this.findExportItem(LogType.system).tips = exportFTDSLogTip;
                if (this.isSurportAntivirus) {
                    this.findExportItem(LogType.antivirus).tips = exportFTDSLogTip;
                }
                break;
            default:
                this.exportItems.forEach((item) => {
                    item.tips = '';
                });
        }
    }

    findExportItem(logType: LogType): ExportItems {
        return this.exportItems.find((item) => item.type === logType);
    }

    stopTask(logType: LogType): void {
        if (this.isExportByNode) {
            this.controlNodeTableCheck(false);
            if (this.btnMap[`${LogType.system}all`].tip !== this.sysAllLogTipBefore) {
                this.btnMap[`${LogType.system}all`].tip = this.sysAllLogTipBefore;
            }
        }

        this.logConfig[logType].count = 0;
        this.isChecking = false;
        this.isExportingLog = false;
        this.downloadNodes = {};
        this.setCurrentLogType(undefined);

        // 表格中有数据时不隐藏表格
        this.showLogTab = Boolean(this.tableData.length);
        this.tableLoading = false;

        if (this.getpathTimer) {
            clearInterval(this.getpathTimer);
        }

        // 显示按钮
        this.setButtonVisible(true);

        let downloadBtn = logType;
        if (this.isDownloadAll) {
            downloadBtn = `${logType}all` as LogType;
        } else if (this.isDownloadPrivate) {
            downloadBtn = `${logType}private` as LogType;
        } else {
            downloadBtn = logType;
        }
        this.setButtonLoading(downloadBtn, false);

        this.setCardLoading(logType, false);

        this.exportItems.forEach((item) => {
            item.tips = '';
        });
        // 禁用超时检测例外
        this.authService.disableTimeoutExcept();
    }

    // 设置卡片上的loading图标
    setCardLoading(logType: LogType, show: boolean): void {
        const type = (logType === LogType.dha || logType === LogType.hssd || logType === LogType.erase) ? LogType.disk : logType;
        const exportingCard = this.exportItems.find((item) => item.type === type);

        if (exportingCard) {
            exportingCard.isLoading = show;
        }
    }

    setButtonVisible(show: boolean, downloadBtn?: string): void {
        // 日志的6个按钮互斥
        for (const key in this.btnMap) {
            if (Object.prototype.hasOwnProperty.call(this.btnMap, key)) {
                const element = this.btnMap[key];
                // 开始下载时，会传downloadBtn，结束下载时不传
                if (downloadBtn === key) {
                    element.visible = !show;
                } else {
                    element.visible = show;
                }
            }
        }
    }

    // 设置按钮加载状态
    setButtonLoading(btn: string | number, loading: boolean): void {
        this.btnMap[btn].loading = loading;
        this.btnMap[btn].label = loading ? this.i18n.loading : this.btnMap[btn].originLabel;
        this.btnMap[btn].icon = loading ? 'icon-loading' : 'icon-download';
    }

    // 设置当前正在导出的类型，当前选中的日志类型和下载的一致时，才显示表格
    setCurrentLogType(inputLogType: LogType): void {
        let logType = inputLogType;

        // dha 和 hssd设置为硬盘日志
        if (logType === LogType.dha || logType === LogType.hssd) {
            logType = LogType.disk;
        }

        this.currentLogType = logType;
    }

    loadTableData(data: NodeData[] = []): void {
        // 导出失败的文件列表
        let failedList: string[] = [];
        // 判断当前log在表格中是否已存在
        const isLogNotInTable = (logName: string): boolean => !this.tableData.some((tableItem): boolean => tableItem.name === logName);

        // 将导出失败或成功的节点，加入列表
        data.forEach((item) => {
            // failFileList字段只有第一个节点会有
            if (item.failFileList) {
                failedList = item.failFileList.split(';') || [];
            }

            // 遍历已导出日志列表，将日志加入列表
            if (item.history_file) {
                item.history_file.forEach((logItem) => {
                    Object.keys(logItem).forEach((logName) => {
                        // 当前log导出状态
                        const logStatus = Number(logItem[logName]);

                        if ([ResultCode.LOAD_SUCCESS, ResultCode.LOAD_FAIL].includes(logStatus) &&
                            isLogNotInTable(logName)) {
                            this.tableData.push({
                                name: logName,
                                nodeId: item.nodeid,
                                status: this.convertStatus(logStatus),
                            });
                        }
                    });
                });
                return;
            }

            // 没有日志列表的异常状态，将状态为成功、失败，存在节点并且没有在表格中出现的记录加入到表格中
            const isNewTableData = [ResultCode.LOAD_SUCCESS, ResultCode.LOAD_FAIL].includes(item.result) && item.nodeid &&
                !this.tableData.some((tableItem) => (tableItem.name === item.name || tableItem.name === UNPLACEHOLDER) && tableItem.nodeId === item.nodeid);
            if (isNewTableData) {
                this.tableData.push({
                    name: item.name || UNPLACEHOLDER,
                    nodeId: item.nodeid,
                    status: this.convertStatus(item.result),
                });
            }
        });

        // 将失败的加入列表
        failedList.forEach((item) => {
            if (!this.tableData.some((tableItem) => tableItem.name === item)) {
                this.tableData.push({
                    name: item,
                    nodeId: data.find((dataItem) => dataItem.failFileList?.includes(item))?.nodeid,
                    status: DownloadStatus.failed,
                });
            }
        });

        this.dt.updateData(this.tableData, false);

        if (this.dt.displayData.length > 0) {
            this.tableLoading = false;
        }
    }

    // 日志下载状态：成功、失败、下载中
    convertStatus(val: number): any {
        let result = DownloadStatus.failed;
        switch (Number(val)) {
            case ResultCode.EXPORT_SUCCESS:
            case ResultCode.EXPORTING:
            case ResultCode.EXPORT_PARTLY:
            case ResultCode.LOADING:
            case ResultCode.DOWNLOAD_PART:
                result = DownloadStatus.loading;
                break;
            case ResultCode.EXPORT_BUSY:
            case ResultCode.EXPORT_FAIL:
            case ResultCode.OFFLINE:
            case ResultCode.LACK_OF_RAM:
            case ResultCode.CANCELED:
            case ResultCode.LOAD_FAIL:
                result = DownloadStatus.failed;
                break;
            case ResultCode.LOAD_SUCCESS:
                result = DownloadStatus.success;
                break;
            default:
                result = DownloadStatus.failed;
        }

        return result;
    }

    checkExistEmporting(): boolean {
        return this.isExportingDiagnose ||
            this.isExportingLog ||
            this.isExportingRunningData ||
            this.isExportingEraseData ||
            this.isExportingDiskData ||
            this.isExportingPerformanceFile;
    }

    onClose(): void {
        if (this.checkExistEmporting()) {
            this.prompt.promptConfirm({
                title: this.i18n.title,
                content: this.i18n.confirmMsg,
                onOk: () => {
                    this.prompt.close();
                    this.show = false;
                },
            });
        } else {
            this.show = false;
        }
    }

    onHide(): void {
        this.lazyLoadSerevice.destroySelf(this);
    }

    /**
     * 跳转到监控设置页
     */
    jumpToConfigPmsLun(): void {
        if (window.location.href.includes('/settings/monitor')) {
            this.onHide();
            return;
        }

        this.doAction.emit({
            trigger: 'goSetting',
        });
        this.router.navigate(['/settings/monitor']);
    }

    private getPackagePerformanceFileStatus(): Observable<any> {
        // 请求超时次数
        let reqTimeoutCount = 0;
        const startTime = Date.now();

        // 总体查询时间超过最长时间60分钟，报错
        const pollingTimeout = (observer: Subscriber<void>): boolean => {
            const now = Date.now();
            if (now - startTime > timeoutMap.performanceFile.max) {
                const errorMsg = {
                    description: this.i18n.baseDesc,
                };
                observer.error(errorMsg);
                observer.complete();
                return false;
            }
            return true;
        };

        return new Observable((observer) => {
            const getStatus = (): void => {
                const successFn = (response: { data: { status: PackagePerformanceFileStatus } }): void => {
                    reqTimeoutCount = 0;

                    if (!pollingTimeout(observer)) {
                        return;
                    }
                    const { data: { status } } = response;
                    if (status === PackagePerformanceFileStatus.idle) {
                        observer.next(response);
                        observer.complete();
                    } else if (Object.keys(this.cannotDownloadPerformanceFileStatus).includes(status)) {
                        observer.error({ description: this.cannotDownloadPerformanceFileStatus[status] });
                        observer.complete();
                    } else {
                        setTimeout(getStatus, timeoutMap.performanceFile.interval);
                    }
                };

                const errorFn = (): void => {
                    if (!pollingTimeout(observer)) {
                        return;
                    }
                    // 超时3次后，报错
                    if (reqTimeoutCount++ >= 3) {
                        observer.error({
                            result: {
                                description: this.i18n.exportFailedReasonDesc,
                                suggestion: this.i18n.exportFailedSuggestDesc,
                            },
                        });
                        observer.complete();
                    } else {
                        setTimeout(getStatus, timeoutMap.performanceFile.interval);
                    }
                };

                const options = {
                    timeout: timeoutMap.performanceFile.getpath,
                };

                this.exportService.getPackagePerformanceDataStatus(options)
                    .subscribe(successFn, errorFn);
            };

            getStatus();
        });
    }

    private getDiagnosePath(): Observable<any> {
        // 请求超时次数
        let reqTimeoutCount = 0;
        const startTime = Date.now();

        return Observable.create((observer) => {
            const getpath = (): void => {
                const successFn = (response): void => {
                    observer.next(response);
                    observer.complete();
                };

                const errorFn = (error): void => {
                    const { result: { code } } = error;

                    if (Number(code) === ErrorCode.packing) {
                        reqTimeoutCount = 0;

                        // 总体查询时间超过最长时间10分钟，报错
                        const now = Date.now();
                        if (now - startTime > timeoutMap.diagnose.max) {
                            const errorMsg = {
                                error: {
                                    code: -1,
                                    description: this.i18n.baseDesc,
                                },
                            };

                            observer.next(errorMsg);
                            observer.complete();
                        } else {
                            setTimeout(getpath, timeoutMap.diagnose.interval);
                        }
                    } else {
                        // 超时3次后，报错
                        if (reqTimeoutCount++ >= 3) {
                            const errorMsg = {
                                result: {
                                    code: -1,
                                    description: this.i18n.exportFailedReasonDesc,
                                    suggestion: this.i18n.exportFailedSuggestDesc,
                                },
                            };

                            observer.next(errorMsg);
                            observer.complete();
                        } else {
                            setTimeout(getpath, timeoutMap.diagnose.interval);
                        }
                    }
                };

                const options = {
                    timeout: timeoutMap.diagnose.getpath,
                };

                this.exportService.exportDiagnoseData({ tag: 'getpath' }, options)
                    .subscribe(successFn, errorFn);
            };

            getpath();
        });
    }

    /**
     * 取消导出性能文件
     */
    cancelExportPerfLog = (): void => {
        this.prompt.warning({
            title: this.i18n.cancelExport,
            content: this.i18n.cancelExportPerfContent,
            onOk: () => {
                this.prompt.close();
                this.isCancelExportPerf = true;
                this.exportService.cancelExportPerfLog({ doSuccess: true })
                    .pipe(finalize(() => this.isCancelExportPerf = false))
                    .subscribe(() => {
                        this.unsubscriptPerf();
                        this.resetPerfBtn();
                    });
            },
        });
    };
}
